import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, RefreshCw, Search, User, Mail, Calendar, DollarSign, MessageSquare, ArrowRightLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const KIND_ICON = {
  booking: DollarSign,
  guest: MessageSquare,
  lead: User,
};

const KIND_LABEL = {
  booking: "Booking",
  guest: "Spotlight",
  lead: "Lead",
};

const KIND_ACCENT = {
  booking: "#00f0ff",
  guest: "#a78bfa",
  lead: "#ff0055",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const CardTile = ({ card, onDragStart, onOpen, onMove, columns = [] }) => {
  const Icon = KIND_ICON[card.kind] || User;
  const accent = KIND_ACCENT[card.kind] || "#00f0ff";
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const canMove = card.kind !== "lead";
  const otherColumns = (columns || []).filter((c) => c.id !== card.column);
  return (
    <div
      draggable={card.kind !== "lead"}
      onDragStart={(e) => onDragStart(e, card)}
      onClick={() => onOpen(card)}
      data-testid={`pipeline-card-${card.kind}-${card.id}`}
      className="group relative rounded-xl border border-white/10 bg-[#0a0510] p-4 cursor-grab active:cursor-grabbing hover:border-white/25 hover:bg-white/[0.04] transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.24em] font-semibold border flex-shrink-0"
          style={{ color: accent, borderColor: `${accent}40`, background: `${accent}10` }}
        >
          <Icon size={9} /> {KIND_LABEL[card.kind]}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-[10px] text-white/40 font-mono-tight">
            {fmtDate(card.created_at)}
          </div>
          {onMove && canMove && otherColumns.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMovePickerOpen((v) => !v); }}
                data-testid={`pipeline-card-move-${card.kind}-${card.id}`}
                aria-label="Move card to another column"
                aria-expanded={movePickerOpen}
                title="Move to…"
                className="w-7 h-7 md:w-6 md:h-6 rounded-md flex items-center justify-center transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 touch-manipulation bg-white/[0.08] text-white/70 hover:bg-[#00f0ff]/20 hover:text-[#00f0ff] active:bg-[#00f0ff]/30"
              >
                <ArrowRightLeft size={12} />
              </button>
              {movePickerOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[70]"
                    onClick={(e) => { e.stopPropagation(); setMovePickerOpen(false); }}
                  />
                  <div
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`pipeline-card-move-menu-${card.kind}-${card.id}`}
                    className="absolute right-0 top-full mt-1 z-[75] w-[220px] max-h-[280px] overflow-y-auto rounded-xl border border-white/15 bg-[#0b0715]/98 backdrop-blur-md shadow-2xl shadow-black/60 p-1.5"
                  >
                    <div className="text-[9px] uppercase tracking-[0.24em] text-white/40 px-2 py-1.5">
                      Move to
                    </div>
                    {otherColumns.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMovePickerOpen(false);
                          onMove(card, c.id);
                        }}
                        data-testid={`pipeline-card-move-to-${c.id}-${card.kind}-${card.id}`}
                        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-[12px] hover:bg-white/[0.06] transition-colors"
                      >
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: c.color || "#00f0ff" }}
                        />
                        <span className="truncate">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="font-semibold text-sm leading-snug mb-1 line-clamp-2">{card.title}</div>
      {card.subtitle && (
        <div className="text-[#a395c3] text-xs line-clamp-1">{card.subtitle}</div>
      )}
      {card.email && (
        <div className="text-white/40 text-[11px] mt-2 truncate flex items-center gap-1.5">
          <Mail size={10} /> {card.email}
        </div>
      )}
    </div>
  );
};

const CardDetailModal = ({ card, onClose }) => {
  if (!card) return null;
  const Icon = KIND_ICON[card.kind] || User;
  const accent = KIND_ACCENT[card.kind] || "#00f0ff";
  return (
    <div
      onClick={onClose}
      data-testid="pipeline-detail-backdrop"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pipeline-detail-modal"
        className="relative w-full max-w-lg rounded-2xl bg-[#11091a] border border-white/15 p-6 md:p-8"
      >
        <button
          onClick={onClose}
          data-testid="pipeline-detail-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center text-white/70"
          aria-label="Close"
        >
          ×
        </button>
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border mb-4"
          style={{ color: accent, borderColor: `${accent}40`, background: `${accent}10` }}
        >
          <Icon size={11} /> {KIND_LABEL[card.kind]}
        </div>
        <div className="font-display font-bold text-2xl mb-2 leading-tight">{card.title}</div>
        {card.subtitle && <div className="text-[#a395c3] text-sm mb-5">{card.subtitle}</div>}

        <div className="space-y-3 text-sm">
          {card.email && <Row icon={Mail} label="Email" value={card.email} />}
          {card.phone && <Row icon={User} label="Phone" value={card.phone} />}
          {card.preferred_date && (
            <Row icon={Calendar} label="Preferred" value={`${card.preferred_date}${card.preferred_time ? ` @ ${card.preferred_time}` : ""}`} />
          )}
          {card.budget > 0 && <Row icon={DollarSign} label="Budget" value={`$${card.budget}`} />}
          {card.brief && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-1.5">Brief</div>
              <div className="rounded-xl bg-black/40 border border-white/10 p-3 text-white/85 max-h-52 overflow-auto whitespace-pre-wrap">
                {card.brief}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-white/50 flex-shrink-0">
      <Icon size={13} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">{label}</div>
      <div className="text-white/90 truncate">{value}</div>
    </div>
  </div>
);

export default function AdminPipeline() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [dragging, setDragging] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/pipeline/board`, { headers: authHeaders() });
      setBoard(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, card) => {
    setDragging(card);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", card.id); } catch { /* Safari fallback */ }
  };

  const handleMove = async (card, colId) => {
    if (!card || card.column === colId) return;
    if (card.kind === "lead") {
      toast.error("Leads can't move columns yet. Convert to a booking first.");
      return;
    }

    // Optimistic update
    setBoard((prev) => {
      if (!prev) return prev;
      const next = { ...prev, cards: { ...prev.cards } };
      Object.keys(next.cards).forEach((c) => {
        next.cards[c] = next.cards[c].filter((x) => !(x.kind === card.kind && x.id === card.id));
      });
      next.cards[colId] = [{ ...card, column: colId }, ...(next.cards[colId] || [])];
      next.totals = Object.fromEntries(Object.entries(next.cards).map(([k, v]) => [k, v.length]));
      return next;
    });

    try {
      await axios.patch(
        `${API}/admin/pipeline/items/${card.kind}/${card.id}/move`,
        { column: colId },
        { headers: authHeaders() },
      );
      toast.success(`Moved to ${colId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed — refresh to resync");
      load();
    }
  };

  const onDrop = async (e, colId) => {
    e.preventDefault();
    setOverCol(null);
    const card = dragging;
    setDragging(null);
    await handleMove(card, colId);
  };

  const filtered = useMemo(() => {
    if (!board) return null;
    if (!query.trim()) return board.cards;
    const q = query.toLowerCase();
    const out = {};
    Object.entries(board.cards).forEach(([col, cards]) => {
      out[col] = cards.filter(
        (c) =>
          (c.title || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.subtitle || "").toLowerCase().includes(q),
      );
    });
    return out;
  }, [board, query]);

  const columns = board?.columns || [];

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full bg-[#a78bfa]/8 blur-[120px]" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <Link to="/admin" data-testid="pipeline-back" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={14} /> Command Center
            </Link>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter">
              Studio <span className="text-[#00f0ff]">pipeline.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              Drag cards between columns. Every move updates status + fires the same emails you had with Monday.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start lg:self-auto">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                data-testid="pipeline-search"
                className="pl-9 pr-3 py-2 rounded-full bg-white/[0.04] border border-white/15 text-sm w-56 focus:outline-none focus:border-[#00f0ff]/50"
              />
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="pipeline-refresh"
              className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center disabled:opacity-50"
              aria-label="Refresh"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-[#ff0055]/30 bg-[#ff0055]/10 p-4 text-sm text-[#ff6b7a] mb-6">
            {error}
          </div>
        )}

        <div
          className="overflow-x-auto pb-8 -mx-5 md:-mx-10 px-5 md:px-10"
          data-testid="pipeline-scroll"
        >
          <div className="flex gap-4 min-w-max">
            {columns.map((col) => {
              const items = (filtered && filtered[col.id]) || [];
              const isOver = overCol === col.id;
              return (
                <div
                  key={col.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (overCol !== col.id) setOverCol(col.id);
                  }}
                  onDragLeave={() => setOverCol((v) => (v === col.id ? null : v))}
                  onDrop={(e) => onDrop(e, col.id)}
                  data-testid={`pipeline-col-${col.id}`}
                  className={`w-[300px] flex-shrink-0 rounded-2xl border transition-colors ${
                    isOver
                      ? "border-[#00f0ff]/60 bg-white/[0.05]"
                      : "border-white/10 bg-white/[0.02]"
                  }`}
                >
                  <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: col.color }}
                      />
                      <div className="font-semibold text-sm">{col.label}</div>
                    </div>
                    <div className="text-xs text-white/50 font-mono-tight">
                      {items.length}
                    </div>
                  </div>
                  <div className="px-3 pb-3 space-y-2 max-h-[70vh] overflow-y-auto">
                    {items.length === 0 && (
                      <div className="text-[11px] text-white/30 py-6 text-center italic">
                        Drop a card here
                      </div>
                    )}
                    {items.map((c) => (
                      <CardTile
                        key={`${c.kind}-${c.id}`}
                        card={c}
                        onDragStart={onDragStart}
                        onOpen={setDetail}
                        onMove={handleMove}
                        columns={columns}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <CardDetailModal card={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
